<template>
  <Message severity="info" :closable="false">{{ $t('Keep this window open and scan documents using your scanner. They will appear in the list below after scanning is done.') }}</Message>
  <div class="flex flex-column relative" style="min-height: 200px;">
    <div v-if="files && preview" class="my-2 grid overflow-y-auto" style="max-height: 50vh">
      <div v-for="file in files" :class="filesGridClasses">
        <FileCard :showRemove="!disabled" :file-url="file.fileName" @remove="removeFile" />
      </div>
    </div>
    <Loader :is-loading="isLoading" :is-local-loader="true" />
    <div v-if="files.length > 0" class="mt-3 flex justify-content-end">
      <button @click="saveAndClose" class="p-button p-component p-button-success">
        <span class="p-button-label">{{ $t('Save') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import Loading from 'vue3-loading-overlay';
import FileCard from '@/components/cards/FileCard.vue';
import Loader from '@/components/common/Loader.vue';
import { b64toBlob } from '@/js/utils';
import {SignalRHub} from '@/services/signalr-hub';


export default {
  name: 'ScannerUpload',
  components: {
    Loader,
    FileCard,
    Loading,
  },
  props: {
    label: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: '',
    },
    error: {
      default: null,
    },
    hideLabel: {
      type: Boolean,
      default: false,
    },
    document: {
      type: Object,
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isMultiple: {
      type: Boolean,
      default: true,
    },
    preview: {
      type: Boolean,
      default: true,
    },
    maxFiles: {
      type: Number,
      default: 0,
    },
    maxFileSize: {
      type: Number,
      default: 0, // in MegaBytes
    },
    acceptedMimeTypes: {
      type: String,
      default: '*',
    },
    rejectedMimeTypes: {
      type: String,
      default: null,
    },
    filesGridClasses: {
      type: String,
      default: 'grid-column col-6 sm:col-6 md:col-4 lg:col-3 xl:col-3 p-1',
    },
    simulateHub: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['removeFile', 'filesSelected', 'closeDialog'],
  computed: {},
  data() {
    return {
      isLoading: false,
      isDragOver: false,
      fileName: undefined,
      hub: null,
      files: [],
      tempFiles:[]
    };
  },
  mounted() {
    if (this.simulateHub) {
      this.startSimulation();
    } else {
      this.hub = new SignalRHub();
      this.hub.on('fileCreated', this.onFileCreated);
      this.hub.on('fileChunk', this.onFileChunk);
      this.hub.on('fileCompleted', this.onFileCompleted);
      this.hub.start();
    }
  },
  methods: {
    startSimulation() {
      // Simulate 2-3 files coming from the scanner
      setTimeout(() => this.simulateFile('document_001.pdf', 2), 1000);
      setTimeout(() => this.simulateFile('scanned_page_002.pdf', 3), 2500);
      setTimeout(() => this.simulateFile('invoice_003.pdf', 2), 4000);
    },
    simulateFile(fileName, numChunks) {
      // Create a simple PDF-like content (mock data)
      const mockPdfHeader = '%PDF-1.4\n';
      const mockPdfContent = `1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n3 0 obj\n<< /Type /Page /Parent 2 0 R /Resources << /Font << /F1 << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> >> >> /MediaBox [0 0 612 792] /Contents 4 0 R >>\nendobj\n4 0 obj\n<< /Length 44 >>\nstream\nBT\n/F1 12 Tf\n100 700 Td\n(Simulated file: ${fileName}) Tj\nET\nendstream\nendobj\nxref\n0 5\n0000000000 65535 f\n0000000009 00000 n\n0000000058 00000 n\n0000000115 00000 n\n0000000317 00000 n\ntrailer\n<< /Size 5 /Root 1 0 R >>\nstartxref\n408\n%%EOF`;
      const fullContent = mockPdfHeader + mockPdfContent;

      // Calculate total size
      const totalSize = fullContent.length;

      // Trigger fileCreated event
      this.onFileCreated({
        fileName,
        size: totalSize,
        totalChunks: numChunks,
      });

      // Split content into chunks and send them
      const chunkSize = Math.ceil(fullContent.length / numChunks);
      for (let i = 0; i < numChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, fullContent.length);
        const chunkContent = fullContent.substring(start, end);

        // Convert chunk to base64
        const base64Chunk = btoa(chunkContent);

        setTimeout(() => {
          this.onFileChunk({
            fileName,
            chunkIndex: i,
            chunk: base64Chunk,
            totalChunks: numChunks,
          });

          // If this is the last chunk, trigger fileCompleted
          if (i === numChunks - 1) {
            setTimeout(() => {
              this.onFileCompleted({ fileName });
            }, 200);
          }
        }, (i + 1) * 300);
      }
    },
    onFileCreated(data) {
      const { fileName, size, totalChunks } = data;
      if (!fileName || !totalChunks) return;

      this.isLoading = true;

      this.tempFiles.push({
        fileName,
        size: Number(size || 0),
        totalChunks: Number(totalChunks),
        receivedChunks: 0,
        receivedBytes: 0,
        parts: [],
        state: 'receiving',
        uploading: false,
        done: false,
      });
    },
    onFileChunk(data) {
      const { fileName, chunkIndex, chunk, totalChunks } = data || {};
      const fileExists = this.tempFiles.find((x) => x.fileName === fileName);
      if (!fileExists) return;

      try {
        const bytes = b64toBlob(String(chunk));
        fileExists.parts[chunkIndex] = bytes;
        fileExists.receivedChunks = Math.max(fileExists.receivedChunks, chunkIndex + 1);
        fileExists.receivedBytes += bytes.byteLength;
        fileExists.totalChunks = Number(totalChunks || fileExists.totalChunks);
      } catch (error) {
        fileExists.state = 'error';
        fileExists.error = error?.message || 'decode error';
      }
    },
    onFileCompleted(data) {
      const fileExists = this.tempFiles.find((x) => x.fileName === data.fileName);
      if (!fileExists) return;

      try {
        fileExists.state = 'assembling';
        const merged = new Blob(fileExists.parts, { type: 'application/pdf' });
        fileExists.blob = merged;
        fileExists.file = new File([merged], fileExists.fileName, { type: 'application/pdf' });
        fileExists.state = 'ready';

        this.files.push(fileExists)
        this.tempFiles.splice(this.tempFiles.indexOf(fileExists), 1);

        // Set isLoading to false when all files are completed
        if (this.tempFiles.length === 0) {
          this.isLoading = false;
        }
      } catch (error) {
        fileExists.state = 'error';
        fileExists.error = error?.message || 'assemble error';
      }
    },
    getFileName(fileName) {
      if (fileName) {
        return fileName.split('/').pop();
      } else return this.$t('View file');
    },
    removeFile(fileToRemove) {
      const index = this.files.findIndex((f) => f.fileName === fileToRemove);
      if (index !== -1) {
        this.files.splice(index, 1);
      }
      this.$emit('removeFile', fileToRemove);
    },
    saveAndClose() {
      this.$emit('filesSelected', this.files);
      this.$emit('closeDialog');
    },
  },
  beforeUnmount() {
    if (!this.simulateHub && this.hub) {
      this.hub.off('fileCreated', this.onFileCreated);
      this.hub.off('fileChunk', this.onFileChunk);
      this.hub.off('fileCompleted', this.onFileCompleted);
      this.hub.stop();
    }
  },
};
</script>

<style lang="scss"></style>
